import type { JSX, PointerEvent as ReactPointerEvent } from 'react';
import { t } from '../messages';

/**
 * Les trois pastilles et le titre. **Cœur uniquement** : une app ne dessine jamais sa barre de
 * titre ni son bouton de fermeture (ADR-006) — c'est la coque qui possède la fenêtre.
 *
 * Les trois pastilles font vraiment les trois choses qu'elles annoncent : fermer ramène au
 * bureau, réduire cache la fenêtre en gardant l'app lancée (son point reste allumé dans le dock),
 * la verte passe en plein écran. Aucun bouton décoratif.
 *
 * La barre elle-même est la poignée de la fenêtre : `onWindowPointerDown/Move/Up` viennent de
 * `useWindowDrag` (posé par `AppWindow`) et ne font rien d'autre que déplacer `.window`.
 */

interface LightSpec {
  readonly key: 'close' | 'minimize' | 'fullscreen';
  readonly stroke: string;
  readonly d: string;
}

/** Valeurs verrouillées : docs/design/desktop-reference.md, § « Barre de titre ».
 *
 * Les trois **fonds** (`#F2685C`, `#F5BE4F`, `#61C555`) sont passés dans `window.css`
 * (`.light--close/--minimize/--fullscreen`) le 7 septembre 2026, en même temps que le
 * multi-fenêtres : une fenêtre du second plan éteint ses pastilles (`--line`), et un style en
 * ligne l'aurait toujours emporté sur cette règle. Les valeurs n'ont pas changé. */
const LIGHTS: readonly LightSpec[] = [
  { key: 'close', stroke: '#7a1e14', d: 'M2.5 2.5l5 5M7.5 2.5l-5 5' },
  { key: 'minimize', stroke: '#8a5a00', d: 'M2.5 5h5' },
  { key: 'fullscreen', stroke: '#1c5c14', d: 'M3.2 6.8h3.6V3.2' },
];

export interface TitleBarProps {
  title: string;
  /** Complément affiché en `--faint` juste après le titre (ici : la classe). */
  subtitle?: string;
  fullscreen: boolean;
  onClose: () => void;
  onMinimize: () => void;
  onToggleFullscreen: () => void;
  onWindowPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onWindowPointerMove: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onWindowPointerUp: (event: ReactPointerEvent<HTMLDivElement>) => void;
}

export function TitleBar({
  title,
  subtitle,
  fullscreen,
  onClose,
  onMinimize,
  onToggleFullscreen,
  onWindowPointerDown,
  onWindowPointerMove,
  onWindowPointerUp,
}: TitleBarProps): JSX.Element {
  const label: Record<LightSpec['key'], string> = {
    close: t('window.close'),
    minimize: t('window.minimize'),
    fullscreen: fullscreen ? t('window.exitFullscreen') : t('window.fullscreen'),
  };
  const action: Record<LightSpec['key'], () => void> = {
    close: onClose,
    minimize: onMinimize,
    fullscreen: onToggleFullscreen,
  };

  return (
    <div
      className={fullscreen ? 'titlebar titlebar--fullscreen' : 'titlebar'}
      onPointerDown={onWindowPointerDown}
      onPointerMove={onWindowPointerMove}
      onPointerUp={onWindowPointerUp}
      onPointerCancel={onWindowPointerUp}
    >
      <div className="lights">
        {LIGHTS.map((light) => (
          <button
            key={light.key}
            type="button"
            className={`light light--${light.key}`}
            onClick={action[light.key]}
            title={label[light.key]}
            aria-label={label[light.key]}
          >
            <svg
              width="7"
              height="7"
              viewBox="0 0 10 10"
              fill="none"
              stroke={light.stroke}
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <path d={light.d} />
            </svg>
          </button>
        ))}
      </div>
      {/* Centré en absolu et inerte : le titre ne doit jamais intercepter un clic destiné à la
          barre elle-même. */}
      <div className="title">
        {title}
        {subtitle === undefined ? null : <span>{` · ${subtitle}`}</span>}
      </div>
    </div>
  );
}
